import React, { useState, useEffect } from "react";
import {
    useUpdateCreditShareMutation,
    useCreateCreditShareMutation,
    CreditShare,
    UpdateCreditShareRequest,
    CreateCreditShareRequest,
} from "../api";
import { useNotification } from "../hooks/useNotification";
import { UserAutocomplete } from "./UserAutocomplete";

interface CreditSharesModalProps {
    isOpen: boolean;
    onClose: () => void;
    creditShare: CreditShare | null;
    mode: "create" | "edit" | "delete";
    onDelete: () => void;
    onSuccess: () => void;
}

export const CreditSharesModal: React.FC<CreditSharesModalProps> = ({
    isOpen,
    onClose,
    creditShare,
    mode,
    onDelete,
    onSuccess,
}) => {
    const { showError, showSuccess } = useNotification();
    const [formData, setFormData] = useState<Partial<CreateCreditShareRequest>>({
        quantity: creditShare?.quantity ?? 0,
        money: creditShare?.money ?? 0,
        day: creditShare?.day ?? 1,
        paid: creditShare?.paid ?? 0,
        payed: creditShare?.payed ?? 0,
        start: creditShare?.start,
        comment: creditShare?.comment || "",
        immunity: creditShare?.immunity ?? false,
        user_id: creditShare?.user_id,
    });

    const [updateCreditShare, { isLoading: isUpdating }] =
        useUpdateCreditShareMutation();
    const [createCreditShare, { isLoading: isCreating }] =
        useCreateCreditShareMutation();

    const isLoading = isUpdating || isCreating;

    useEffect(() => {
        if (creditShare) {
            setFormData({
                quantity: creditShare.quantity,
                money: creditShare.money,
                day: creditShare.day,
                paid: creditShare.paid,
                payed: creditShare.payed,
                start: creditShare.start,
                comment: creditShare.comment || "",
                immunity: creditShare.immunity ?? false,
                user_id: creditShare.user_id,
            });
        } else if (mode === "create") {
            setFormData({
                quantity: 0,
                money: 0,
                day: 1,
                paid: 0,
                payed: 0,
                start: undefined,
                comment: "",
                immunity: false,
                user_id: undefined,
            });
        }
    }, [creditShare, mode]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (mode === "delete" && creditShare) {
            onDelete();
            return;
        }

        try {
            if (mode === "create") {
                if (!formData.user_id) {
                    showError("Выберите пользователя");
                    return;
                }
                await createCreditShare(formData as CreateCreditShareRequest).unwrap();
                showSuccess("Кредитные акции созданы успешно");
            } else if (mode === "edit" && creditShare) {
                await updateCreditShare({
                    userId: creditShare.user_id,
                    data: formData as UpdateCreditShareRequest,
                }).unwrap();
                showSuccess("Информация обновлена успешно");
            }
            onSuccess();
        } catch (error: any) {
            showError(
                error?.data?.message ||
                    error?.message ||
                    "Ошибка при выполнении операции"
            );
        }
    };

    const formatDateForInput = (timestamp: number | null): string => {
        if (!timestamp) return "";
        const date = new Date(timestamp * 1000);
        return date.toISOString().split("T")[0];
    };

    const handleDateChange = (value: string) => {
        if (!value) {
            setFormData({ ...formData, start: undefined });
            return;
        }
        const timestamp = Math.floor(new Date(value).getTime() / 1000);
        setFormData({ ...formData, start: timestamp });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {mode === "create"
                            ? "Добавить кредитные акции"
                            : mode === "edit"
                            ? "Редактировать кредитные акции"
                            : "Удалить кредитные акции"}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {mode === "delete" ? (
                    <div className="p-6">
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Вы уверены, что хотите удалить кредитные акции для
                            пользователя{" "}
                            {creditShare?.user
                                ? `${creditShare.user.name} ${creditShare.user.surname}`
                                : `ID: ${creditShare?.user_id}`}
                            ?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                            >
                                {isLoading ? "Удаление..." : "Удалить"}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Пользователь
                                </label>
                                {mode === "create" ? (
                                    <UserAutocomplete
                                        value={formData.user_id?.toString() || ""}
                                        onChange={(id) => setFormData({ ...formData, user_id: parseInt(id) })}
                                        placeholder="Выберите пользователя..."
                                    />
                                ) : (
                                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-sm text-gray-900 dark:text-white">
                                        {creditShare?.user
                                            ? `${creditShare.user.name} ${creditShare.user.surname} (${creditShare.user.email})`
                                            : `ID: ${creditShare?.user_id}`}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Количество акций
                                </label>
                                <input
                                    type="number"
                                    value={formData.quantity ?? ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            quantity: parseInt(e.target.value) || 0,
                                        })
                                    }
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Сумма ежемесячного платежа (₮)
                                </label>
                                <input
                                    type="number"
                                    value={formData.money ?? ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            money: parseInt(e.target.value) || 0,
                                        })
                                    }
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    День платежа (1-31)
                                </label>
                                <input
                                    type="number"
                                    value={formData.day ?? ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            day: parseInt(e.target.value) || 1,
                                        })
                                    }
                                    min="1"
                                    max="31"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Количество оплаченных месяцев
                                </label>
                                <input
                                    type="number"
                                    value={formData.paid ?? ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            paid: parseInt(e.target.value) || 0,
                                        })
                                    }
                                    min="0"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Дата следующего платежа
                                </label>
                                <input
                                    type="date"
                                    value={formatDateForInput(formData.start ?? null)}
                                    onChange={(e) => handleDateChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Комментарий
                                </label>
                                <textarea
                                    value={formData.comment ?? ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            comment: e.target.value,
                                        })
                                    }
                                    rows={3}
                                    maxLength={255}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="immunity"
                                    checked={formData.immunity ?? false}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            immunity: e.target.checked,
                                        })
                                    }
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                />
                                <label
                                    htmlFor="immunity"
                                    className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Иммунитет
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                Отмена
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {isLoading ? "Сохранение..." : "Сохранить"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

